import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, AlertOctagon, ChevronRight, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const BADGE = { unpaid: "Unpaid", assessment_feedback: "Blocking", assessment_missing: "Blocking" };

const TONE = {
  red: { color: "#FF4500", Icon: AlertOctagon, label: "Overdue" },
  amber: { color: "#FF9F1C", Icon: AlertTriangle, label: "Review" },
};

const CATEGORY = {
  assessment_missing: "Awaiting assessment booking",
  assessment_feedback: "Awaiting assessment result",
  feedback_due: "Session feedback due",
  unpaid: "Unpaid sessions",
  target_not_set: "Target level not set",
  focus_area: "Flagged skills",
};

export const AttentionList = () => {
  const [items, setItems] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  useEffect(() => { api.get("/coach/attention").then((r) => setItems(r.data?.items || [])).catch(() => setItems([])); }, []);
  if (!items.length) return null;
  const dismiss = async (e, it) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.post("/coach/attention/dismiss", { key: it.dismiss_key });
      setItems((cur) => cur.filter((x) => x.dismiss_key !== it.dismiss_key));
      toast.success("Removed from your list — the player's record is unchanged");
    } catch {
      toast.error("Couldn't dismiss that item");
    }
  };
  const red = items.filter((i) => i.severity === "red").length;
  const groups = [];
  items.forEach((it, i) => {
    let g = groups.find((x) => x.kind === it.kind);
    if (!g) { g = { kind: it.kind, rows: [] }; groups.push(g); }
    g.rows.push({ it, i });
  });
  return (
    <section className="card-surface p-5 sm:p-6 border-l-4 fade-up" style={{ borderLeftColor: red ? TONE.red.color : TONE.amber.color }} data-testid="attention-section">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <h2 className="text-base md:text-lg font-bold tracking-tight">Requires your attention</h2>
        <span className="text-xs text-[#A3A3A3]" data-testid="attention-count">{items.length} item{items.length === 1 ? "" : "s"}{red ? ` · ${red} overdue` : ""}</span>
      </div>
      <div className="space-y-2">
        {groups.map((g) => {
          const isOpen = !collapsed[g.kind];
          return (
            <div key={g.kind} data-testid={`attention-group-${g.kind}`}>
              <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [g.kind]: isOpen }))} aria-expanded={isOpen}
                data-testid={`attention-group-toggle-${g.kind}`}
                className="w-full flex items-center gap-2 py-2 text-left text-[11px] uppercase tracking-wider font-bold text-[#A3A3A3] hover:text-white transition-colors border-b border-[#1F1F1F]">
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                <span className="flex-1">{CATEGORY[g.kind] || g.kind.replace(/_/g, " ")}</span>
                <span className="text-[#737373]">{g.rows.length}</span>
              </button>
              {isOpen && (
                <ul className="divide-y divide-[#1F1F1F]">
                  {g.rows.map(({ it, i }) => {
                    const t = TONE[it.severity] || TONE.amber;
                    return (
                      <li key={`${it.kind}-${i}`} className="flex items-center gap-2">
                        <Link to={it.to} className="group flex flex-1 min-w-0 items-center gap-3 py-3 hover:bg-[#141414] -mx-2 px-2 transition-colors" data-testid={`attention-item-${it.kind}-${i}`}>
                          <t.Icon className="w-4 h-4 shrink-0" style={{ color: t.color }} />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold truncate">{it.title}</div>
                            <div className="text-xs text-[#A3A3A3] truncate">{it.detail}</div>
                          </div>
                          <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border shrink-0" style={{ color: t.color, borderColor: `${t.color}66` }}>{it.severity === "red" ? (BADGE[it.kind] || t.label) : t.label}</span>
                          <ChevronRight className="w-4 h-4 text-[#525252] group-hover:text-white transition-transform group-hover:translate-x-0.5" />
                        </Link>
                        {it.dismiss_key && (
                          <button type="button" onClick={(e) => dismiss(e, it)} className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[#404040] text-[#A3A3A3] hover:text-white hover:border-white shrink-0" data-testid={`attention-dismiss-${it.dismiss_key.split(":")[1]}`}>
                            Dismiss
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
